import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { change, reduxForm } from 'redux-form';
import {
	getCompaniesOptionsSelector,
	getCompaniesSearchValueSelector,
	getCompaniesSelector,
	getCompaniesTypesSelector,
	getCompanyTaxSystemInfoSelector,
	getFormInitialValuesSelector,
	getIsCompaniesLoadingSelector,
	getIsCompaniesTypesLoadingSelector,
	getIsCompanyInfoLoadingSelector,
	getIsOrgTypesLoadingSelector,
	getIsProfileCompanyFormSelector,
	getIsTaxRatesLoadingSelector,
	getIsTaxSystemsLoadingSelector,
	getOrgTypesSelector,
	getTaxRatesSelector,
	getTaxSystemsSelector,
} from '../../selectors';

import { FORM_NAME } from '../../constants';
import {
	getCompaniesAction,
	getTaxRatesAction,
	setCompaniesAction,
	setCompaniesSearchValueAction,
	setOrgTypesAction,
	setTaxRatesAction,
	setTaxSystemsAction,
} from '../../actions';
import { CompanyFormView } from './company-form-view';
import { validate } from '../../utils';

const CompanyFormWrapper = ({
	isCompanyInfoLoading,
	companiesTypes,
	isCompaniesTypesLoading,
	companiesOptions,
	companiesSearchValue,
	isCompaniesLoading,
	isProfileCompanyForm,
	companies,
	onFieldChange,
	onGetCompanies,
	onSetCompaniesSearchValue,
	onSetCompanies,
	orgTypes,
	isOrgTypesLoading,
	taxSystems,
	isTaxSystemsLoading,
	taxRates,
	isTaxRatesLoading,
	onGetTaxRates,
	initialValues,
	onSetTaxRates,
}) => {
	useEffect(() => {
		if (initialValues.company?.tax_system_id) {
			onGetTaxRates({ taxSystemId: initialValues.company.tax_system_id });
		}

		return () => {
			onSetCompaniesSearchValue('');
			onSetCompanies([]);
		};
	}, [initialValues.company?.tax_system_id]);

	const handleCompanySearchChange = ({ target: { value } }) => {
		onSetCompaniesSearchValue(value);
		onGetCompanies();
	};

	const handleCompanyChange = value => {
		const currentCompany = companies.find(({ data }) => data.inn === value);

		if (currentCompany) {
			onFieldChange(FORM_NAME, 'company.name', currentCompany.value);
			onFieldChange(FORM_NAME, 'company.inn', currentCompany.data?.inn);
			onFieldChange(FORM_NAME, 'company.ogrn', currentCompany.data?.ogrn);
			onFieldChange(FORM_NAME, 'company.address', currentCompany.data?.address?.value);
			onSetCompaniesSearchValue(currentCompany.value);
		}
	};

	const handleTaxSystemChange = value => {
		if (value) {
			onFieldChange(FORM_NAME, 'company.tax_rate_id', '');
			onSetTaxRates([]);
			onGetTaxRates({ taxSystemId: value });
		} else {
			onFieldChange(FORM_NAME, 'company.tax_rate_id', '');
			onSetTaxRates([]);
		}
	};

	return (
		<CompanyFormView
			isCompanyInfoLoading={isCompanyInfoLoading}
			companiesTypes={companiesTypes}
			isCompaniesTypesLoading={isCompaniesTypesLoading}
			companiesOptions={companiesOptions}
			onCompanySearchChange={handleCompanySearchChange}
			onCompanyChange={handleCompanyChange}
			companiesSearchValue={companiesSearchValue}
			isCompaniesLoading={isCompaniesLoading}
			isProfileCompanyForm={isProfileCompanyForm}
			orgTypes={orgTypes}
			isOrgTypesLoading={isOrgTypesLoading}
			taxSystems={taxSystems}
			isTaxSystemsLoading={isTaxSystemsLoading}
			taxRates={taxRates}
			isTaxRatesLoading={isTaxRatesLoading}
			onGetTaxRates={onGetTaxRates}
			onTaxSystemChange={handleTaxSystemChange}
		/>
	);
};

const withForm = reduxForm({
	form: FORM_NAME,
	enableReinitialize: true,
	onSubmit: validate,
})(CompanyFormWrapper);

const mapStateToProps = createStructuredSelector({
	isCompanyInfoLoading: getIsCompanyInfoLoadingSelector(),
	companiesTypes: getCompaniesTypesSelector(),
	isCompaniesTypesLoading: getIsCompaniesTypesLoadingSelector(),
	companiesOptions: getCompaniesOptionsSelector(),
	companiesSearchValue: getCompaniesSearchValueSelector(),
	isCompaniesLoading: getIsCompaniesLoadingSelector(),
	isProfileCompanyForm: getIsProfileCompanyFormSelector(),
	companies: getCompaniesSelector(),
	initialValues: getFormInitialValuesSelector(),
	companyTaxInfo: getCompanyTaxSystemInfoSelector(),
	orgTypes: getOrgTypesSelector(),
	isOrgTypesLoading: getIsOrgTypesLoadingSelector(),
	taxSystems: getTaxSystemsSelector(),
	isTaxSystemsLoading: getIsTaxSystemsLoadingSelector(),
	taxRates: getTaxRatesSelector(),
	isTaxRatesLoading: getIsTaxRatesLoadingSelector(),
});

const mapDispatchToProps = {
	onFieldChange: change,
	onGetCompanies: getCompaniesAction,
	onSetCompaniesSearchValue: setCompaniesSearchValueAction,
	onSetCompanies: setCompaniesAction,
	onSetOrgTypes: setOrgTypesAction,
	onSetTaxSystems: setTaxSystemsAction,
	onGetTaxRates: getTaxRatesAction,
	onSetTaxRates: setTaxRatesAction,
};

const CompanyForm = connect(mapStateToProps, mapDispatchToProps)(withForm);

CompanyFormWrapper.propTypes = {
	isCompaniesTypesLoading: PropTypes.bool.isRequired,
	isCompanyInfoLoading: PropTypes.bool.isRequired,
	companiesTypes: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))),
	companiesOptions: PropTypes.arrayOf(
		PropTypes.shape({
			key: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired,
			text: PropTypes.string,
			data: PropTypes.shape({
				title: PropTypes.string.isRequired,
				address: PropTypes.string.isRequired,
				deleted: PropTypes.bool,
			}),
		}),
	).isRequired,
	isCompaniesLoading: PropTypes.bool.isRequired,
	companiesSearchValue: PropTypes.string.isRequired,
	isProfileCompanyForm: PropTypes.bool.isRequired,
	companies: PropTypes.array.isRequired,
	onFieldChange: PropTypes.func.isRequired,
	onGetCompanies: PropTypes.func.isRequired,
	onSetCompaniesSearchValue: PropTypes.func.isRequired,
	onSetCompanies: PropTypes.func.isRequired,
	onGetTaxRates: PropTypes.func.isRequired,
	onSetTaxRates: PropTypes.func.isRequired,
	orgTypes: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		}),
	),
	isOrgTypesLoading: PropTypes.bool,
	taxSystems: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		}),
	),
	isTaxSystemsLoading: PropTypes.bool,
	taxRates: PropTypes.arrayOf(
		PropTypes.shape({
			text: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
		}),
	),
	isTaxRatesLoading: PropTypes.bool,
	initialValues: PropTypes.object,
};

export { CompanyForm };
